import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Calendar, Download, MessageCircle, Image as ImageIcon, Send, FileText, CheckCircle, XCircle, Loader2, FileDown, FileSignature, ExternalLink, Receipt, DollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const ClientPortal = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [quote, setQuote] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [quotesInvoices, setQuotesInvoices] = useState({ quotes: [], invoices: [] });
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [processingQuote, setProcessingQuote] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const messagesEndRef = useRef(null);

  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const downloadQuotePdf = async () => {
    setDownloadingPdf('quote');
    try {
      const response = await axios.get(`${API}/quotes/portal/${token}/quote-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${quote?.quote_id || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Quote PDF downloaded");
    } catch (error) {
      toast.error("Failed to download quote PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const downloadInvoicePdf = async () => {
    setDownloadingPdf('invoice');
    try {
      const response = await axios.get(`${API}/quotes/portal/${token}/invoice-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      toast.error("Failed to download invoice PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  useEffect(() => {
    fetchPortalData();
    fetchMessages();
    fetchQuote();
    
    // Check for payment result
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (paymentStatus === "success") {
      // Verify the payment with the backend
      verifyPayment(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment was cancelled.");
    }
  }, [token]);

  const verifyPayment = async (sessionId) => {
    setVerifyingPayment(true);
    try {
      const response = await axios.post(`${API}/quotes/portal/${token}/verify-payment`, {
        session_id: sessionId
      });
      
      const result = response.data;
      
      if (result.status === "paid" || result.status === "already_paid") {
        toast.success(result.message || "Payment successful! Your booking is confirmed.");
        // Refresh data to show updated status
        fetchPortalData();
        fetchQuote();
        fetchMessages();
      } else if (result.status === "pending") {
        toast.info("Your payment is still being processed. Please refresh in a moment.");
      } else if (result.status === "not_found") {
        toast.warning("Payment verification pending. Please contact the photographer if this persists.");
      } else {
        toast.error(result.message || "Could not verify payment");
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      toast.error("Could not verify payment. Please contact the photographer.");
    } finally {
      setVerifyingPayment(false);
      // Clear the URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchPortalData = async () => {
    try {
      const response = await axios.get(`${API}/portal/${token}`);
      setData(response.data);
    } catch (error) {
      toast.error("Invalid or expired link");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/bookings/portal/${token}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages");
    }
  };

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/quotes/portal/${token}`);
      setQuote(response.data);
    } catch (error) {
      // No quote yet
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/portal/${token}/contracts`);
      setContracts(response.data.contracts || []);
    } catch (error) {
      // No contracts
    }
  };

  const fetchQuotesInvoices = async () => {
    try {
      const response = await axios.get(`${API}/portal/${token}/quotes-invoices`);
      setQuotesInvoices(response.data || { quotes: [], invoices: [] });
    } catch (error) {
      // No quotes/invoices
    }
  };

  // Add contracts fetch to initial load
  useEffect(() => {
    if (data) {
      fetchContracts();
      fetchQuotesInvoices();
    }
  }, [data]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/bookings/portal/${token}/messages`, {
        content: newMessage
      });
      toast.success("Message sent!");
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleAcceptQuote = async () => {
    setProcessingQuote(true);
    try {
      const response = await axios.post(`${API}/quotes/portal/${token}/accept`);
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process quote");
      setProcessingQuote(false);
    }
  };

  const handleDeclineQuote = async () => {
    setProcessingQuote(true);
    try {
      await axios.post(`${API}/quotes/portal/${token}/decline`, {
        reason: declineReason
      });
      toast.success("Quote declined");
      setDeclineDialogOpen(false);
      fetchQuote();
      fetchPortalData();
    } catch (error) {
      toast.error("Failed to decline quote");
    } finally {
      setProcessingQuote(false);
    }
  };

  const downloadCalendar = () => {
    window.open(`${API}/portal/${token}/calendar`, "_blank");
  };

  const getStatusBadge = (status) => {
    const styles = {
      // New state machine statuses
      received: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      quoted: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      deposit_paid: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
      event_completed: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      gallery_delivered: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
      paid_in_full: "bg-green-500/20 text-green-400 border-green-500/30",
      archived: "bg-gray-500/20 text-gray-400 border-gray-500/30",
      // Legacy statuses
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      declined: "bg-red-500/20 text-red-400 border-red-500/30",
      needs_info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      in_discussion: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      quote_sent: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      quote_declined: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return styles[status] || styles.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      received: "Received",
      quoted: "Quote Sent",
      deposit_paid: "Deposit Paid",
      confirmed: "Confirmed",
      event_completed: "Event Completed",
      gallery_delivered: "Gallery Delivered",
      paid_in_full: "Paid in Full",
      archived: "Archived"
    };
    return labels[status] || status.replace("_", " ");
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-2xl mb-4">Link Not Found</h1>
          <p className="text-muted-foreground mb-6">This link may have expired or is invalid.</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (data.type === "booking") {
    const { booking, photographer, messages: oldMessages, galleries } = data;
    
    // Dynamic branding
    const brandName = photographer?.company_name || "360Lumina";
    const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;

    return (
      <div className="min-h-screen py-12 px-6" data-testid="client-portal">
        {/* Header */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center gap-3 mb-6">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="h-8 max-w-[140px] object-contain" />
            ) : (
              <>
                <Camera className="w-8 h-8 text-primary" />
                <span className="text-2xl font-serif">{brandName}</span>
              </>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Your Booking</h1>
          <p className="text-muted-foreground">
            Photographer: {photographer?.user?.name}
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid gap-8">
          {/* Status Card */}
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-serif">Booking Status</CardTitle>
                <Badge className={`${getStatusBadge(booking.status)} border`}>
                  {getStatusLabel(booking.status)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Event Type</p>
                  <p className="font-medium">{booking.event_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Services</p>
                  <p className="font-medium">{booking.request_types?.join(", ")}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Date</p>
                  <p className="font-medium">
                    {booking.is_tournament
                      ? `${booking.tournament_start_date} - ${booking.tournament_end_date}`
                      : booking.event_date || "To be confirmed"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Venue</p>
                  <p className="font-medium">{booking.venue_name || "To be confirmed"}</p>
                </div>
              </div>

              {booking.status === "confirmed" && (
                <div className="mt-6 pt-6 border-t border-border">
                  <Button onClick={downloadCalendar} data-testid="download-calendar">
                    <Calendar className="w-4 h-4 mr-2" />
                    Add to Calendar
                  </Button>
                </div>
              )}

              {booking.status === "declined" && booking.decline_reason && (
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-1">Reason</p>
                  <p>{booking.decline_reason}</p>
                  {booking.alternative_dates && (
                    <>
                      <p className="text-sm text-muted-foreground mt-4 mb-1">Alternative Dates</p>
                      <p>{booking.alternative_dates}</p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quote Card */}
          {quote && quote.status !== "draft" && (
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-serif flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Quote
                  </CardTitle>
                  <Badge className={`border ${
                    quote.status === "sent" || quote.status === "viewed" 
                      ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
                      : quote.status === "accepted" || quote.status === "paid"
                      ? "bg-green-500/20 text-green-400 border-green-500/30"
                      : "bg-red-500/20 text-red-400 border-red-500/30"
                  }`}>
                    {quote.status === "paid" ? "Deposit Paid" : quote.status.replace("_", " ")}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">{quote.package_name}</h3>
                  {quote.package_description && (
                    <p className="text-muted-foreground mb-4">{quote.package_description}</p>
                  )}
                  
                  {/* Line Items */}
                  <div className="border rounded-lg overflow-hidden mb-4">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2 text-left">Item</th>
                          <th className="px-4 py-2 text-center">Qty</th>
                          <th className="px-4 py-2 text-right">Price</th>
                          <th className="px-4 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quote.line_items?.map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-2">{item.description}</td>
                            <td className="px-4 py-2 text-center">{item.quantity}</td>
                            <td className="px-4 py-2 text-right">${item.unit_price?.toFixed(2)}</td>
                            <td className="px-4 py-2 text-right">${(item.quantity * item.unit_price).toFixed(2)}</td>
                          </tr>
                        ))}
                        {quote.travel_fee > 0 && (
                          <tr className="border-t">
                            <td className="px-4 py-2" colSpan="3">Travel Fee</td>
                            <td className="px-4 py-2 text-right">${quote.travel_fee.toFixed(2)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Totals */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>${quote.subtotal?.toFixed(2)}</span>
                    </div>
                    {quote.discount > 0 && (
                      <div className="flex justify-between text-green-500">
                        <span>Discount</span>
                        <span>-${quote.discount.toFixed(2)}</span>
                      </div>
                    )}
                    {/* Show Stripe-collected tax if available, otherwise quote estimate, or disclaimer */}
                    {(quote.stripe_tax_amount > 0 || quote.deposit_tax_amount > 0) ? (
                      <div className="flex justify-between">
                        <span>Tax (collected)</span>
                        <span>${(quote.stripe_tax_amount || quote.deposit_tax_amount).toFixed(2)}</span>
                      </div>
                    ) : quote.tax_amount > 0 ? (
                      <div className="flex justify-between">
                        <span>Tax ({quote.tax_rate || 0}%)</span>
                        <span>${quote.tax_amount.toFixed(2)}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between text-muted-foreground text-sm">
                        <span>Tax</span>
                        <span>Calculated at checkout*</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                      <span>Total</span>
                      <span>${(quote.stripe_total || quote.total)?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Deposit Required ({quote.deposit_percentage}%)</span>
                      <span>${quote.deposit_amount?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Balance Due at Session</span>
                      <span>${quote.balance_due?.toFixed(2)}</span>
                    </div>
                    {/* Tax disclaimer for unpaid quotes */}
                    {quote.status !== 'paid' && !quote.tax_amount && !quote.stripe_tax_amount && (
                      <p className="text-xs text-muted-foreground mt-2">
                        * Tax will be calculated based on your billing address during checkout.
                      </p>
                    )}
                  </div>
                  
                  {/* Deliverables */}
                  <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium mb-2">What&apos;s Included</h4>
                    <p className="text-sm text-muted-foreground">{quote.deliverables}</p>
                    <p className="text-sm mt-2">
                      <span className="text-muted-foreground">Turnaround:</span> {quote.turnaround_days} days
                    </p>
                  </div>
                  
                  {/* Non-refundable notice for deposits */}
                  {(quote.status === "sent" || quote.status === "viewed") && (
                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-sm text-yellow-400">
                        ⚠️ <strong>Note:</strong> Deposits are non-refundable. Please review the quote carefully before proceeding.
                      </p>
                    </div>
                  )}
                  
                  {/* Expiration */}
                  {quote.expires_at && (quote.status === "sent" || quote.status === "viewed") && (
                    <p className="text-sm text-muted-foreground mt-4">
                      Quote valid until: {new Date(quote.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                
                {/* Actions */}
                {(quote.status === "sent" || quote.status === "viewed") && (
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={handleAcceptQuote} disabled={processingQuote} className="flex-1 sm:flex-none">
                      {processingQuote ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Pay Deposit & Lock Your Date
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setDeclineDialogOpen(true)}
                      disabled={processingQuote}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={downloadQuotePdf}
                      disabled={downloadingPdf === 'quote'}
                      data-testid="download-quote-pdf"
                    >
                      {downloadingPdf === 'quote' ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <FileDown className="w-4 h-4 mr-1" />
                      )}
                      Download PDF
                    </Button>
                  </div>
                )}
                
                {quote.status === "paid" && !booking.final_payment_paid && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-blue-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Deposit Paid - Booking Confirmed!</span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={downloadQuotePdf}
                          disabled={downloadingPdf === 'quote'}
                        >
                          {downloadingPdf === 'quote' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={downloadInvoicePdf}
                          disabled={downloadingPdf === 'invoice'}
                          data-testid="download-invoice-pdf"
                        >
                          {downloadingPdf === 'invoice' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">Balance due: ${quote.balance_due?.toFixed(2)}</p>
                    <Link to={`/portal/${token}/invoice`}>
                      <Button size="sm" data-testid="view-invoice-btn">
                        View Invoice & Pay Final Balance
                      </Button>
                    </Link>
                  </div>
                )}
                
                {booking.status === "paid_in_full" || booking.final_payment_paid ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">✅ Paid in Full - Thank you!</span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={downloadQuotePdf}
                          disabled={downloadingPdf === 'quote'}
                          title="Download Quote"
                        >
                          {downloadingPdf === 'quote' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={downloadInvoicePdf}
                          disabled={downloadingPdf === 'invoice'}
                          title="Download Invoice"
                        >
                          {downloadingPdf === 'invoice' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Messages */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-xl font-serif flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                Messages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-6 max-h-80 overflow-y-auto">
                {messages?.length > 0 ? (
                  messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex flex-col ${
                        msg.sender_type === "client" ? "items-end" : "items-start"
                      }`}
                    >
                      {msg.sender_type === "system" ? (
                        <div className="w-full text-center">
                          <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                            {msg.content}
                          </span>
                        </div>
                      ) : (
                        <>
                          <div
                            className={`max-w-[80%] rounded-lg px-4 py-2 ${
                              msg.sender_type === "client"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted/30"
                            }`}
                          >
                            <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                          </div>
                          <span className="text-xs text-muted-foreground mt-1">
                            {msg.sender_type === "client" ? "You" : msg.sender_name} • {formatTime(msg.created_at)}
                          </span>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">No messages yet</p>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="flex gap-2">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  rows={2}
                  data-testid="message-input"
                />
                <Button onClick={handleSendMessage} disabled={sending} data-testid="send-message">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Decline Quote Dialog */}
          <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Decline Quote</DialogTitle>
                <DialogDescription>
                  Are you sure you want to decline this quote? You can optionally provide a reason.
                </DialogDescription>
              </DialogHeader>
              <Textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Reason for declining (optional)..."
                rows={3}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeclineDialogOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDeclineQuote} disabled={processingQuote}>
                  {processingQuote && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Decline Quote
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delivered Galleries */}
          {galleries?.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-xl font-serif flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-primary" />
                  Your Galleries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {galleries.map((gallery) => (
                    <Link
                      key={gallery.gallery_id}
                      to={`/portal/${gallery.magic_link_token}`}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors"
                    >
                      <div>
                        <p className="font-medium">{gallery.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {gallery.event_date}
                          {gallery.expires_at && ` • Expires ${new Date(gallery.expires_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm">
                        View Gallery
                      </Button>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contracts Section */}
          {contracts.length > 0 && (
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <FileSignature className="w-5 h-5" />
                  Contracts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {contracts.map((contract) => (
                    <div
                      key={contract.contract_id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-sm"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{contract.template_name}</p>
                          <Badge 
                            className={`text-xs ${
                              contract.status === "signed" 
                                ? "bg-green-500/20 text-green-400 border-green-500/30" 
                                : contract.status === "pending"
                                ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                                : "bg-gray-500/20 text-gray-400 border-gray-500/30"
                            }`}
                          >
                            {contract.status === "signed" ? "Signed" : contract.status === "pending" ? "Pending" : contract.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {contract.status === "signed" && contract.signed_at
                            ? `Signed on ${new Date(contract.signed_at).toLocaleDateString()}`
                            : `Sent on ${new Date(contract.created_at).toLocaleDateString()}`
                          }
                        </p>
                      </div>
                      {contract.status === "pending" && (
                        <a 
                          href={`/sign/${contract.contract_id}?token=${contract.signing_token || ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm">
                            <FileSignature className="w-4 h-4 mr-1" />
                            Sign Now
                          </Button>
                        </a>
                      )}
                      {contract.status === "signed" && (
                        <Button variant="ghost" size="sm" disabled>
                          <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                          Complete
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quotes & Invoices Section */}
          {(quotesInvoices.quotes?.length > 0 || quotesInvoices.invoices?.length > 0) && (
            <Card className="bg-card/50 border-border" data-testid="quotes-invoices-section">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Quotes & Invoices
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Quotes */}
                  {quotesInvoices.quotes?.map((q) => (
                    <div
                      key={q.quote_id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-sm"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <p className="font-medium">{q.package_name || "Quote"}</p>
                          <Badge 
                            className={`text-xs ${
                              q.status === "paid" 
                                ? "bg-green-500/20 text-green-400 border-green-500/30" 
                                : q.status === "sent" || q.status === "viewed"
                                ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                                : q.status === "accepted"
                                ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                                : q.status === "declined"
                                ? "bg-red-500/20 text-red-400 border-red-500/30"
                                : "bg-gray-500/20 text-gray-400 border-gray-500/30"
                            }`}
                          >
                            {q.status === "paid" ? "Paid" : q.status === "sent" ? "Pending" : q.status}
                          </Badge>
                          {q.type === "AD_HOC" && (
                            <Badge variant="outline" className="text-xs">
                              Ad-Hoc Charge
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          ${q.total?.toFixed(2)} • {new Date(q.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            window.open(`${API}/quotes/portal/${token}/quote-pdf`, '_blank');
                          }}
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                        {(q.status === "sent" || q.status === "viewed") && !q.type?.includes("AD_HOC") && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handleAcceptQuote}
                            disabled={processingQuote}
                          >
                            {processingQuote ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <DollarSign className="w-4 h-4 mr-1" />
                            )}
                            Pay Now
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Invoices */}
                  {quotesInvoices.invoices?.map((inv) => (
                    <div
                      key={inv.invoice_id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-sm"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-muted-foreground" />
                          <p className="font-medium">{inv.package_name || "Invoice"}</p>
                          <Badge 
                            className={`text-xs ${
                              inv.status === "paid" 
                                ? "bg-green-500/20 text-green-400 border-green-500/30" 
                                : inv.status === "partial"
                                ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                                : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                            }`}
                          >
                            {inv.status === "paid" ? "Paid in Full" : inv.status === "partial" ? "Deposit Paid" : "Due"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Total: ${inv.total?.toFixed(2)}
                          {inv.balance_due > 0 && ` • Balance Due: $${inv.balance_due?.toFixed(2)}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            window.open(`${API}/quotes/portal/${token}/invoice-pdf`, '_blank');
                          }}
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                        {inv.balance_due > 0 && (
                          <Link to={`/portal/${token}/invoice`}>
                            <Button size="sm" variant="outline">
                              <DollarSign className="w-4 h-4 mr-1" />
                              Pay Balance
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}

                  {quotesInvoices.quotes?.length === 0 && quotesInvoices.invoices?.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">
                      No quotes or invoices yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Gallery view
  if (data.type === "gallery") {
    const { gallery, assets, photographer } = data;
    
    // Dynamic branding
    const brandName = photographer?.company_name || "360Lumina";
    const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;

    return (
      <div className="min-h-screen py-12 px-6" data-testid="client-gallery">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="h-8 max-w-[140px] object-contain" />
            ) : (
              <>
                <Camera className="w-8 h-8 text-primary" />
                <span className="text-2xl font-serif">{brandName}</span>
              </>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">{gallery.title}</h1>
          <p className="text-muted-foreground mb-8">
            {assets?.length || 0} photos
            {gallery.expires_at && ` • Available until ${new Date(gallery.expires_at).toLocaleDateString()}`}
          </p>

          {assets?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {assets.map((asset, index) => (
                <motion.a
                  key={asset.asset_id}
                  href={`${API}/media/${gallery.gallery_id}/${asset.filename}?token=${token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className="relative group"
                >
                  <div className="aspect-square bg-muted rounded-sm overflow-hidden">
                    <img
                      src={asset.thumbnail_path 
                        ? `${API.replace('/api', '')}${asset.thumbnail_path}?token=${token}`
                        : `${API}/media/${gallery.gallery_id}/${asset.filename}?token=${token}`
                      }
                      alt={asset.filename}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Download className="w-6 h-6" />
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          ) : (
            <Card className="bg-card border-border">
              <CardContent className="py-16 text-center">
                <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-lg">No photos in this gallery yet</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default ClientPortal;
